import {
  AfterViewInit,
  ChangeDetectorRef,
  Directive,
  ElementRef,
  EventEmitter,
  Injector,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  afterEveryRender,
  afterNextRender,
  inject,
} from '@angular/core';

/** The height of a table row before one was measured. */
export const DEFAULT_ROW_HEIGHT = 41;
/** Rows rendered above and below the visible ones. */
export const OVERSCAN_ROWS = 20;
/** The rendered range moves in steps of this many rows (an even number). */
const RANGE_STEP = 10;
/** Rows rendered while the table is not visible (e.g. a hidden tab). */
const HIDDEN_ROWS = 40;

export interface RowRange {
  start: number;
  end: number;
}

/**
 * The rows to render for the visible rows `first` to `last` (exclusive) of
 * `count`: the visible ones and `overscan` rows around them, the start
 * rounded down and the end rounded up to steps of RANGE_STEP rows, so the
 * range changes only every few rows of scrolling. The start stays even, so
 * the stripes of the rows do not change on the way.
 */
export const rowRange = (
  first: number,
  last: number,
  count: number,
  overscan = OVERSCAN_ROWS
): RowRange => {
  const start = Math.max(
    0,
    Math.floor(Math.max(0, first - overscan) / RANGE_STEP) * RANGE_STEP
  );
  const end = Math.min(
    count,
    Math.ceil((Math.max(last, first) + overscan) / RANGE_STEP) * RANGE_STEP
  );
  return { start: Math.min(start, end - (end % 2)), end };
};

/** Whether the element can scroll its content vertically itself. */
const scrolls = (element: HTMLElement) => {
  const { overflowY } = getComputedStyle(element);
  return (
    (overflowY === 'auto' || overflowY === 'scroll') &&
    element.scrollHeight > element.clientHeight
  );
};

/**
 * Renders only the rows of a long table that are in view (windowing): on the
 * scrolling `.app-table-wrapper` of a table, with the `count` of rows. The
 * template renders the rows from `start` to `end` and two spacer bodies of
 * `before` and `after` pixels around them, the first one with the class
 * `virtual-before`; the rows carry `data-row-index`. Header, column widths,
 * sticky header and the table layout stay as they are.
 *
 * Rows have one height (no line breaks); the directive measures it from the
 * rendered rows. It works in a wrapper that scrolls (the table that fills the
 * window) as well as in the page flow, where the window or the main area
 * scrolls (phones).
 *
 * `nearEnd` is emitted once per row count when the visible rows come within
 * `nearEndRows` of the end, so the page loads the next rows in time.
 */
@Directive({
  selector: '[appVirtualRows]',
  exportAs: 'appVirtualRows',
  standalone: true,
})
export class VirtualRowsDirective
  implements OnChanges, AfterViewInit, OnDestroy
{
  private element = inject<ElementRef<HTMLElement>>(ElementRef);
  private zone = inject(NgZone);
  private changeDetector = inject(ChangeDetectorRef);
  private injector = inject(Injector);

  /** The number of rows of the table. */
  @Input('appVirtualRows') count = 0;
  @Input() overscan = OVERSCAN_ROWS;
  @Input() nearEndRows = 50;
  @Output() nearEnd = new EventEmitter<void>();

  public start = 0;
  public end = 0;
  public rowHeight = DEFAULT_ROW_HEIGHT;

  private frame = 0;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private nearEndCount = -1;
  private removeListeners: (() => void) | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private renderRef = afterEveryRender({ read: () => this.measure() });

  /** The height of the rows above the rendered ones. */
  public get before() {
    return this.start * this.rowHeight;
  }

  /** The height of the rows below the rendered ones. */
  public get after() {
    return Math.max(0, this.count - this.end) * this.rowHeight;
  }

  ngOnChanges() {
    if (this.count < this.nearEndCount) {
      this.nearEndCount = -1;
    }
    // The rows to render now; scrolling or the next frame refines them.
    this.applyRange(this.visibleRange());
    this.schedule();
  }

  ngAfterViewInit() {
    const element = this.element.nativeElement;
    this.zone.runOutsideAngular(() => {
      const onScroll = () => this.schedule();
      element.addEventListener('scroll', onScroll, { passive: true });
      // The window or the main area scrolls the table in the page flow.
      window.addEventListener('scroll', onScroll, {
        capture: true,
        passive: true,
      });
      window.addEventListener('resize', onScroll, { passive: true });
      this.removeListeners = () => {
        element.removeEventListener('scroll', onScroll);
        window.removeEventListener('scroll', onScroll, { capture: true });
        window.removeEventListener('resize', onScroll);
      };
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(onScroll);
        this.resizeObserver.observe(element);
      }
    });
    this.schedule();
  }

  ngOnDestroy() {
    cancelAnimationFrame(this.frame);
    clearTimeout(this.timer);
    this.removeListeners?.();
    this.resizeObserver?.disconnect();
    this.renderRef.destroy();
  }

  /** The scroll offset of the wrapper, null when the page scrolls it. */
  public get scrollOffset(): number | null {
    const element = this.element.nativeElement;
    return scrolls(element) ? element.scrollTop : null;
  }

  /** Back to the first row, for another filter or sorting. */
  public scrollToTop() {
    const element = this.element.nativeElement;
    element.scrollTop = 0;
    if (!scrolls(element) && element.getBoundingClientRect().top < 0) {
      element.scrollIntoView({ block: 'start' });
    }
    this.applyRange(rowRange(0, HIDDEN_ROWS, this.count, this.overscan));
    this.schedule();
  }

  /**
   * Shows the row with this index: at the scroll offset the table had, if
   * given and the row is in view there, else in the middle of the view.
   */
  public reveal(index: number, offset: number | null = null) {
    const element = this.element.nativeElement;
    if (scrolls(element)) {
      if (offset !== null) {
        element.scrollTop = offset;
      }
      const { first, last } = this.visibleRange();
      if (index < first || index >= last - 1) {
        const header = element.querySelector<HTMLElement>('thead');
        const view = element.clientHeight - (header?.offsetHeight ?? 0);
        element.scrollTop = Math.max(
          0,
          (index + 0.5) * this.rowHeight - view / 2
        );
      }
      this.applyRange(this.visibleRange());
    } else {
      this.applyRange(rowRange(index, index + 1, this.count, this.overscan));
    }
    this.changeDetector.markForCheck();
    afterNextRender(
      {
        read: () => {
          const row = element.querySelector<HTMLElement>(
            `tr[data-row-index="${index}"]`
          );
          if (row && !this.inView(row)) {
            row.scrollIntoView({ block: 'center' });
          }
          this.schedule();
        },
      },
      { injector: this.injector }
    );
  }

  /**
   * Updates the range on the next frame, or after a short time where frames
   * pause (a hidden window still scrolled by a script).
   */
  private schedule() {
    if (this.frame) {
      return;
    }
    const run = () => {
      cancelAnimationFrame(this.frame);
      clearTimeout(this.timer);
      this.frame = 0;
      this.update();
    };
    this.zone.runOutsideAngular(() => {
      this.frame = requestAnimationFrame(run);
      this.timer = setTimeout(run, 100);
    });
  }

  private update() {
    const range = this.visibleRange();
    if (
      this.count > 0 &&
      range.last >= this.count - this.nearEndRows &&
      this.nearEndCount !== this.count
    ) {
      this.nearEndCount = this.count;
      this.zone.run(() => this.nearEnd.emit());
    }
    if (this.applyRange(range)) {
      this.zone.run(() => this.changeDetector.markForCheck());
    }
  }

  /** The visible rows and the range to render for them. */
  private visibleRange(): RowRange & { first: number; last: number } {
    const element = this.element.nativeElement;
    const anchor = element.querySelector<HTMLElement>('.virtual-before');
    const box = element.getBoundingClientRect();
    if (!anchor || box.height <= 0) {
      const first = this.start;
      const last = first + HIDDEN_ROWS;
      return {
        first,
        last,
        ...rowRange(first, last, this.count, this.overscan),
      };
    }
    const header = element.querySelector<HTMLElement>('thead');
    const top = Math.max(box.top + (header?.offsetHeight ?? 0), 0);
    const bottom = Math.min(box.bottom, window.innerHeight);
    // Where the first row of the whole table would be.
    const origin = anchor.getBoundingClientRect().top;
    const first = Math.max(0, Math.floor((top - origin) / this.rowHeight));
    const last = Math.max(
      first,
      Math.ceil((Math.max(bottom, top) - origin) / this.rowHeight)
    );
    return { first, last, ...rowRange(first, last, this.count, this.overscan) };
  }

  private applyRange({ start, end }: RowRange) {
    if (start === this.start && end === this.end) {
      return false;
    }
    this.start = start;
    this.end = end;
    return true;
  }

  private inView(row: HTMLElement) {
    const element = this.element.nativeElement;
    const box = element.getBoundingClientRect();
    const header = element.querySelector<HTMLElement>('thead');
    const rect = row.getBoundingClientRect();
    return (
      rect.top >= Math.max(box.top + (header?.offsetHeight ?? 0), 0) &&
      rect.bottom <= Math.min(box.bottom, window.innerHeight)
    );
  }

  /** The row height from the rendered rows, after each rendering. */
  private measure() {
    const rows =
      this.element.nativeElement.querySelectorAll<HTMLElement>(
        'tr[data-row-index]'
      );
    if (rows.length < 2) {
      return;
    }
    const first = rows[0].getBoundingClientRect();
    const last = rows[rows.length - 1].getBoundingClientRect();
    const height = (last.bottom - first.top) / rows.length;
    if (height > 0 && Math.abs(height - this.rowHeight) > 0.5) {
      this.rowHeight = height;
      this.zone.run(() => this.changeDetector.markForCheck());
      this.schedule();
    }
  }
}
